/* eslint no-process-env: 0 */

import '@/utils/slick.css';
import Head from 'next/head';
import '@/styles/globals.css';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { logger } from '@/utils/logger';
import localFont from 'next/font/local';
import { useRouter } from 'next/router';
import type { AppProps } from 'next/app';
import { env } from '@zopauk/nextjs-env';
import { Inter } from 'next/font/google';
import { TextLink } from '@/components/TextLink';
import { Navbar } from '@/components/Navigation';
import { Amplitude } from '@/components/Amplitude';
import { GA4Session } from '@/components/GA4Session';
import ErrorBoundary from '@/components/ErrorBoundary';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { SiteSwitcher } from '@/components/SiteSwitcher';
import { isBusinessRoute } from '@/utils/business-route';
import { AmazonConnect } from '@/components/AmazonConnect';
import { useBucketCookie } from '@/utils/ab/persistBucket';
import { useHashScroll } from '@/utils/hooks/useHashScroll';
import { useGA4PageView } from '@/utils/hooks/useGA4PageView';
import { GlobalAppStyles } from '@/components/GlobalAppStyles';
import { InformationBanner } from '@/components/InformationBanner';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SessionExpiryModal, LoadingSection, Footer } from '@zopauk/web-ui';
import { B2C_FAVICON_URL, B2B_FAVICON_URL, getFavicon } from '@/utils/getFavicon';
import { type Environments, SessionProvider, type Session } from '@zopauk/nextjs-auth';
import { type DehydratedState, QueryClientProvider, HydrationBoundary, QueryClient } from '@tanstack/react-query';

// Loaded client-side only: @zopauk/libs-otel/client pulls in the Honeycomb web
// SDK (honeycomb-web 1.5.0), which references `window` at module load and would
// crash `next build` if evaluated server-side. It only initialises telemetry in
// a useEffect, so SSR gains nothing.
const Honeycomb = dynamic(() => import('@zopauk/libs-otel/client').then((mod) => mod.Honeycomb), { ssr: false });

const inter = Inter({
  weight: ['400', '500', '600', '700', '800'],
  variable: '--sans-font',
  subsets: ['latin'],
  display: 'swap',
});

const seasonMix = localFont({
  src: [
    {
      path: '../fonts/season-mix/SeasonMix-SemiBold.woff2',
      style: 'normal',
      weight: '600',
    },
  ],
  variable: '--font-serif',
  display: 'swap',
});

type PageProps = {
  dehydratedState?: DehydratedState;
  session?: Session | null;
};

const ZopaApp = ({ pageProps: { session, ...pageProps }, Component }: AppProps<PageProps>) => {
  const router = useRouter();
  const b2bEnabled = env('NEXT_PUBLIC_B2B_ENABLED') === 'enabled';
  const isPreviewMode = router.isPreview;
  const isBusiness = (b2bEnabled || isPreviewMode) && isBusinessRoute(router.asPath);
  const favicon = getFavicon(isBusiness ? B2B_FAVICON_URL : B2C_FAVICON_URL);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: Number.parseInt(env('NEXT_PUBLIC_REACT_QUERY_RETRIES') || '3', 10),
            refetchOnWindowFocus: false,
            refetchInterval: false,
            staleTime: 60000 * 5, // 5 min
            retryDelay: 500,
          },
        },
      }),
  );

  useHashScroll();
  // A/B bucket cookie: assigned by Fastly; switched to control when the visitor declines (see persistBucket.ts).
  useBucketCookie();
  useGA4PageView();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          {env('NEXT_PUBLIC_APP_ENV') === 'development' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
          <Head>
            <meta content="width=device-width, initial-scale=1, shrink-to-fit=no" name="viewport" />
            <link href="/favicon.ico" sizes="any" rel="icon" />
            <link href={isBusiness ? '/site-b2b.webmanifest' : '/site.webmanifest'} rel="manifest" />
            <link type="image/svg+xml" href={favicon.svg} rel="icon" />
            <link href={favicon.png32} type="image/png" sizes="32x32" rel="icon" />
            <link href={favicon.appleTouchIcon} rel="apple-touch-icon" />
          </Head>
          <SessionProvider ExpiryModal={SessionExpiryModal} Loading={LoadingSection} session={session}>
            <div
              className={`${inter.variable} ${seasonMix.variable}`}
              {...(isBusiness && { 'data-theme': 'business' })}
            >
              <GlobalAppStyles />

              <Amplitude />
              <GA4Session />
              <SiteSwitcher />
              <Navbar isBusiness={isBusiness} />
              <InformationBanner />

              <div className="main-content" id="main-content">
                <Component {...pageProps} />
              </div>

              <Footer renderLink={(props) => <TextLink {...props} />} isBusiness={isBusiness} />

              <AmazonConnect />
            </div>

            {['production', 'uat'].includes(env('NEXT_PUBLIC_APP_ENV') || '') ? (
              <Honeycomb
                serviceVersion={process.env.NEXT_PUBLIC_APP_VERSION || 'development'}
                environment={env('NEXT_PUBLIC_APP_ENV') as Environments}
                token={env('NEXT_PUBLIC_HONEYCOMB_API_KEY') || ''}
                serviceName="galleria-web"
                logger={logger}
              />
            ) : null}
          </SessionProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default ZopaApp;
